import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { cn } from '@/lib/utils';
import { setActiveTab } from '@/modules/tryIt/redux/actions';
import {
  selectActiveTab,
  selectHasAuthentication,
  selectIsReplay,
} from '@/modules/tryIt/redux/selectors';
import { TabId } from '@/modules/tryIt/types/state';

// The Model tab is GA (Model Strategy owns model selection for all accounts),
// so it's always present in test mode — replay has no model override.
const TEST_TABS: TabId[] = [TabId.Details, TabId.Context, TabId.Model];
const REPLAY_TABS: TabId[] = [
  TabId.Details,
  TabId.Context,
  TabId.User,
  TabId.Conversation,
  TabId.Review,
];

const TEST_TABS_WITH_AUTH: TabId[] = [...TEST_TABS, TabId.Authentication];
const REPLAY_TABS_WITH_AUTH: TabId[] = [
  TabId.Details,
  TabId.Context,
  TabId.Authentication,
  TabId.User,
  TabId.Conversation,
  TabId.Review,
];

const labelKeyOf = (tab: TabId, isReplay: boolean) => {
  if (tab === TabId.Details) {
    return 'try_it.headers.turns';
  }
  if (tab === TabId.Authentication && isReplay) {
    return 'try_it.headers.authentication_short';
  }
  return `try_it.headers.${tab}`;
};

/**
 * Right-panel tab bar. Picks the tab set from the mode (test vs replay) and
 * from whether the session has an auth step.
 */
export const Tabs = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isReplay = useSelector(selectIsReplay);
  const hasAuthentication = useSelector(selectHasAuthentication);
  const activeTab = useSelector(selectActiveTab);

  const authTabs = isReplay ? REPLAY_TABS_WITH_AUTH : TEST_TABS_WITH_AUTH;
  const plainTabs = isReplay ? REPLAY_TABS : TEST_TABS;
  const tabs = hasAuthentication ? authTabs : plainTabs;

  return (
    // border-b here, not on the header: the underline spans only the tabs.
    <nav className="flex items-end border-b border-stroke">
      {tabs.map((tab) => {
        const isActive = tab === activeTab;
        return (
          <button
            key={tab}
            onClick={() => dispatch(setActiveTab(tab))}
            className={cn(
              'min-w-14 px-4 pt-2 text-center text-sm leading-none font-semibold transition-colors',
              isActive
                ? 'border-b-2 border-primary pb-3 text-foreground'
                : 'pb-3.5 text-foreground-muted hover:text-foreground'
            )}
          >
            {t(labelKeyOf(tab, isReplay))}
          </button>
        );
      })}
    </nav>
  );
};
